import { ReactNode } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import useToast from "~/components/ui/toast";
import { useFetcher } from "~/hooks/useFetcher";
import {
  DEFAULT_SKILL_CONTENT,
  Skill,
  SKILL_NAME_MAX_LENGTH,
  attachAgentSkillFromFields,
  skillPath,
  updateSkill,
} from "~/services/skills";
import { isApiError } from "~/utils/apiClient";
import { AgentSkillForm, type SkillFormValues, type SkillSubmitResult } from "./AgentSkillForm";

export type AgentSkillEditorMode =
  | { kind: "create"; agentId: string; agentDisplayName: string }
  | { kind: "duplicate"; agentId: string; agentDisplayName: string; sourceId: string }
  | { kind: "edit"; skillId: string };

interface AgentSkillEditorDialogProps {
  mode: AgentSkillEditorMode | null;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

interface ModeMeta {
  title: ReactNode;
  description?: ReactNode;
  saveLabel: string;
}

function getModeMeta(mode: AgentSkillEditorMode, sourceName?: string): ModeMeta {
  switch (mode.kind) {
    case "edit":
      return { title: "Edit Skill", saveLabel: "Save" };
    case "duplicate":
      return {
        title: (
          <span>
            Duplicate <span className="text-muted-foreground">·</span>{" "}
            <span>{sourceName ?? "…"}</span>
          </span>
        ),
        description: `Edit before saving — original is unchanged. This copy will be attached to ${mode.agentDisplayName}.`,
        saveLabel: "Save copy",
      };
    case "create":
      return {
        title: "Create skill",
        description: `This skill will be attached to ${mode.agentDisplayName}.`,
        saveLabel: "Save",
      };
  }
}

function deriveInitial(
  mode: AgentSkillEditorMode,
  sourceSkill: Skill | undefined
): SkillFormValues | null {
  if (mode.kind === "create") {
    return { name: "", description: "", content: DEFAULT_SKILL_CONTENT };
  }
  if (!sourceSkill) {
    return null;
  }
  if (mode.kind === "edit") {
    return {
      name: sourceSkill.name,
      description: sourceSkill.description,
      content: sourceSkill.content,
    };
  }
  return {
    name: `${sourceSkill.name}-copy`.slice(0, SKILL_NAME_MAX_LENGTH),
    description: sourceSkill.description,
    content: sourceSkill.content,
  };
}

const LoaderBlock = () => (
  <div className="flex flex-1 items-center justify-center px-6 py-12">
    <Loader2 className="size-6 animate-spin text-muted-foreground" />
  </div>
);

export const AgentSkillEditorDialog = ({
  mode,
  onOpenChange,
  onSaved,
}: AgentSkillEditorDialogProps) => {
  const { showError, showSuccess } = useToast();

  const sourceSkillId =
    mode?.kind === "edit" ? mode.skillId : mode?.kind === "duplicate" ? mode.sourceId : null;

  const { data: sourceSkill } = useFetcher<Skill>(sourceSkillId ? skillPath(sourceSkillId) : null);

  const open = mode !== null;
  const modeKey = mode === null ? "closed" : `${mode.kind}:${sourceSkillId ?? "new"}`;
  const meta = mode ? getModeMeta(mode, sourceSkill?.name) : null;
  const initial = mode ? deriveInitial(mode, sourceSkill) : null;

  const handleSubmit = async (values: SkillFormValues): Promise<SkillSubmitResult> => {
    if (!mode) {
      return { ok: false };
    }
    try {
      if (mode.kind === "edit") {
        await updateSkill(mode.skillId, values);
        showSuccess("Skill updated");
      } else {
        await attachAgentSkillFromFields(mode.agentId, values);
        showSuccess(
          mode.kind === "duplicate" ? "Skill copied to agent" : "New skill created on agent"
        );
      }
      onSaved();
      return { ok: true };
    } catch (e) {
      if (isApiError(e) && e.status === 409) {
        const message = "A skill with this name already exists on this agent.";
        showError(`${message} Choose a different name.`);
        return { ok: false, field: "name", message };
      }
      showError(e instanceof Error ? e.message : "Unable to save skill");
      return { ok: false };
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] w-[calc(100%-2rem)] flex-col gap-0 overflow-hidden p-0 text-foreground sm:w-[70vw] sm:max-w-[920px]">
        <DialogHeader className="shrink-0 px-6 pt-6 pb-2">
          <DialogTitle>{meta?.title}</DialogTitle>
          {meta?.description && <DialogDescription>{meta.description}</DialogDescription>}
        </DialogHeader>

        {meta && initial !== null ? (
          <AgentSkillForm
            key={modeKey}
            initial={initial}
            saveLabel={meta.saveLabel}
            onSubmit={handleSubmit}
            onCancel={() => onOpenChange(false)}
          />
        ) : (
          <LoaderBlock />
        )}
      </DialogContent>
    </Dialog>
  );
};
